import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode, Upload, Landmark } from "lucide-react";
import api, { apiError, BULAN } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_BADGE = {
  pending: { label: "Menunggu Konfirmasi", cls: "bg-amber-50 text-amber-700 hover:bg-amber-50" },
  approved: { label: "Disetujui", cls: "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" },
  rejected: { label: "Ditolak", cls: "bg-rose-50 text-rose-700 hover:bg-rose-50" },
};

export default function ParentPay() {
  const [info, setInfo] = useState(null);
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState([]);
  const [payTarget, setPayTarget] = useState(null);
  const [amount, setAmount] = useState("");
  const [proof, setProof] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [infoRes, sppRes, billsRes, myRes] = await Promise.all([
        api.get("/payments/online/info"),
        api.get("/spp/my"),
        api.get("/bills/my/list"),
        api.get("/payments/online/my"),
      ]);
      setInfo(infoRes.data);
      setMine(myRes.data);
      const list = [];
      for (const c of sppRes.data.children || []) {
        const sisa = Math.max((sppRes.data.spp_nominal || 0) - c.total_paid, 0);
        if (c.status !== "lunas" && sisa > 0) {
          list.push({ kind: "spp", student: c.student, title: `SPP ${BULAN[sppRes.data.month]} ${sppRes.data.year}`, remaining: sisa });
        }
      }
      for (const entry of billsRes.data || []) {
        for (const item of entry.bills || []) {
          const sisa = Math.max(item.bill.amount - item.total_paid, 0);
          if (item.status !== "lunas" && sisa > 0) {
            list.push({ kind: "bill", student: entry.student, bill: item.bill, title: item.bill.title, remaining: sisa });
          }
        }
      }
      setItems(list);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  useEffect(() => { load(); }, []);

  const openPay = (item) => {
    setPayTarget(item);
    setAmount(String(item.remaining));
    setProof(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!proof) {
      toast.error("Unggah foto bukti pembayaran dulu");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("kind", payTarget.kind);
      fd.append("student_id", payTarget.student.id);
      fd.append("amount", amount);
      if (payTarget.kind === "bill") fd.append("bill_id", payTarget.bill.id);
      fd.append("proof", proof);
      await api.post("/payments/online", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bukti pembayaran terkirim — menunggu konfirmasi bendahara");
      setPayTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const qrisUrl = info?.qris_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${info.qris_path}?auth=${localStorage.getItem("sb_token")}`
    : null;
  const bank = info?.payment_info || {};
  const hasMethod = qrisUrl || bank.account_number;

  return (
    <div className="space-y-6" data-testid="parent-pay-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Bayar Online</h1>
        <p className="mt-1 text-sm text-slate-500">Bayar SPP/tagihan via transfer atau QRIS sekolah, lalu unggah bukti pembayaran.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="payment-methods-card">
          <h3 className="font-heading text-base font-semibold text-slate-800">Cara Pembayaran</h3>
          {!hasMethod && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700" data-testid="no-payment-method">
              Sekolah belum mengatur info pembayaran online. Silakan hubungi bendahara.
            </p>
          )}
          {qrisUrl && (
            <div className="mt-4">
              <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-slate-700"><QrCode className="h-4 w-4 text-[#0060AF]" strokeWidth={1.5} /> Scan QRIS</p>
              <img src={qrisUrl} alt="QRIS sekolah" className="h-48 w-48 rounded-lg border border-slate-200 object-cover" data-testid="qris-image" />
            </div>
          )}
          {bank.account_number && (
            <div className="mt-4 rounded-lg bg-slate-50 p-4" data-testid="bank-info">
              <p className="flex items-center gap-1.5 text-sm font-medium text-slate-700"><Landmark className="h-4 w-4 text-[#0060AF]" strokeWidth={1.5} /> Transfer {bank.bank_name}</p>
              <p className="mt-1 font-mono text-lg font-semibold tracking-wide text-slate-900">{bank.account_number}</p>
              <p className="text-xs text-slate-500">a.n. {bank.account_name}</p>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="unpaid-items-card">
          <h3 className="font-heading text-base font-semibold text-slate-800">Tagihan yang Bisa Dibayar</h3>
          {items.length === 0 && (
            <p className="mt-3 text-sm text-slate-500" data-testid="no-unpaid-items">Tidak ada tagihan tertunda. Semua sudah lunas.</p>
          )}
          <div className="mt-3 space-y-2">
            {items.map((item, i) => (
              <div key={`${item.kind}-${item.student.id}-${item.bill?.id || "spp"}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 p-3"
                   data-testid={`pay-item-${item.kind}-${item.student.nisn}${item.bill ? `-${item.bill.id}` : ""}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{item.title}</p>
                  <p className="text-xs text-slate-500">{item.student.name} ({item.student.class_name}) · sisa Rp {item.remaining.toLocaleString("id-ID")}</p>
                </div>
                <Button size="sm" onClick={() => openPay(item)} data-testid={`pay-button-${item.kind}-${item.student.nisn}${item.bill ? `-${item.bill.id}` : ""}`}
                        className="gap-1 bg-[#0060AF] hover:bg-[#004B87]">
                  <Upload className="h-3.5 w-3.5" strokeWidth={1.5} /> Bayar
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="my-payments-card">
        <h3 className="font-heading text-base font-semibold text-slate-800">Riwayat Pengajuan Anda</h3>
        <div className="mt-3 space-y-2">
          {mine.length === 0 && <p className="text-sm text-slate-400" data-testid="my-payments-empty">Belum ada pengajuan pembayaran online.</p>}
          {mine.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3" data-testid={`my-payment-row-${p.id}`}>
              <div>
                <p className="text-sm font-medium text-slate-800">{p.kind === "spp" ? `SPP ${BULAN[p.month]} ${p.year}` : p.kind === "bill" ? p.bill_title : p.kind === "setor" ? "Setor Tabungan" : "Tarik Tabungan"} — Rp {p.amount.toLocaleString("id-ID")}</p>
                <p className="text-xs text-slate-500">{p.student_name} · {new Date(p.created_at).toLocaleDateString("id-ID")}</p>
                {p.status === "rejected" && p.reject_reason && <p className="text-xs text-rose-500">Alasan: {p.reject_reason}</p>}
              </div>
              <Badge className={`rounded-full ${STATUS_BADGE[p.status]?.cls || ""}`} data-testid={`my-payment-status-${p.id}`}>
                {STATUS_BADGE[p.status]?.label || p.status}
              </Badge>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <DialogContent aria-describedby={undefined} data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bayar — {payTarget?.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="pay-form">
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {payTarget?.student.name} ({payTarget?.student.class_name}) · sisa tagihan Rp {payTarget?.remaining.toLocaleString("id-ID")}
            </div>
            <div className="space-y-2">
              <Label htmlFor="pay-amount">Jumlah yang Dibayar (Rp)</Label>
              <Input id="pay-amount" type="number" min="1" max={payTarget?.remaining} data-testid="pay-amount-input"
                     value={amount} onChange={(e) => setAmount(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pay-proof">Foto Bukti Transfer / QRIS</Label>
              <Input id="pay-proof" type="file" accept=".jpg,.jpeg,.png,.webp" data-testid="pay-proof-input"
                     onChange={(e) => setProof(e.target.files?.[0] || null)} required />
              <p className="text-xs text-slate-400">Format jpg/png/webp, maksimal 2 MB.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPayTarget(null)} data-testid="pay-cancel">Batal</Button>
              <Button type="submit" disabled={sending} data-testid="pay-submit-button" className="bg-[#0060AF] hover:bg-[#004B87]">
                {sending ? "Mengirim..." : "Kirim Bukti Pembayaran"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
